/**
 * Completely dependency-free YouMayAlsoLikeComponent.
 * Uses manual event construction to avoid import-map resolution issues.
 */
class YouMayAlsoLikeComponent extends HTMLElement {
  constructor() {
    super();
    // Static event reference to match theme's events.js
    this.CART_UPDATE_EVENT = 'cart:update';
    this.CART_ERROR_EVENT = 'cart:error';
  }

  connectedCallback() {
    this.addEventListener('change', this.#handleChange.bind(this));
    this.addEventListener('submit', this.#handleAddToCart.bind(this));
    this.addEventListener('click', this.#handleClick.bind(this));

    // Safety: ensure forms don't redirect
    this.querySelectorAll('form').forEach(form => {
      form.onsubmit = (e) => { e.preventDefault(); return false; };
    });

    // Listen to cart update events to restore temporary success states after morphing
    this.cartUpdateListener = () => {
      setTimeout(() => {
        this.#updateButtonSuccessStates();
      }, 100);
    };
    document.addEventListener(this.CART_UPDATE_EVENT, this.cartUpdateListener);
  }

  disconnectedCallback() {
    if (this.cartUpdateListener) {
      document.removeEventListener(this.CART_UPDATE_EVENT, this.cartUpdateListener);
    }
  }

  #handleChange(e) {
    const select = e.target;
    if (select.hasAttribute('data-ymal-size-select')) {
      this.#selectSize(select);
    }
  }

  #handleClick(e) {
    const addBtn = e.target.closest('.you-may-also-like__add-btn');
    if (addBtn) {
      e.preventDefault();
      this.#handleDesktopAddToCart(addBtn);
      return;
    }

    const swatch = e.target.closest('.pcs-block__swatch');
    if (swatch && swatch.closest('you-may-also-like-component') === this) {
      e.preventDefault();
      this.#selectColorSwatch(swatch);
    }

    // Close dropdown if clicking outside or on a different dropdown
    const clickedDropdown = e.target.closest('.ymal-custom-dropdown');
    this.querySelectorAll('.ymal-custom-dropdown[open]').forEach(details => {
      if (details !== clickedDropdown) {
        details.removeAttribute('open');
      }
    });
  }

  async #handleDesktopAddToCart(btn) {
    const card = btn.closest('.ymal-row-wrap');
    if (!card) return;

    const sizeSelect = card.querySelector('[data-ymal-size-select]');
    const variantId = sizeSelect ? sizeSelect.value : null;

    if (!variantId) return;

    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    // Set success state immediately on click for instant visual feedback
    btn.setAttribute('data-added', 'true');
    window.recentlyAddedVariantIds = window.recentlyAddedVariantIds || {};
    window.recentlyAddedVariantIds[variantId.toString()] = Date.now();

    try {
      const formData = new FormData();
      formData.append('id', variantId.toString());
      formData.append('quantity', '1');

      /** @type {string[]} */
      const sectionsToUpdate = [];
      /** @type {NodeListOf<HTMLElement>} */
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach(comp => {
        const sId = comp.dataset.sectionId;
        if (sId && !sectionsToUpdate.includes(sId)) {
          sectionsToUpdate.push(sId);
        }
      });
      if (sectionsToUpdate.length === 0) sectionsToUpdate.push('header');
      formData.append('sections', sectionsToUpdate.join(','));

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      const itemAdded = await response.json();

      if (!response.ok) {
        throw new Error(itemAdded.description || itemAdded.message || 'Error adding to cart');
      }

      // Fetch full cart for tracking and UI sync
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      setTimeout(() => {
        btn.removeAttribute('data-added');
      }, 1500);

      // Manually construct the event to match the structure in events.js
      const source = this.dataset.source || 'you-may-also-like';
      const eventDetail = {
        resource: cart,
        sourceId: source,
        data: {
          sections: itemAdded.sections,
          source: source,
          itemCount: cart.item_count,
          variantId: itemAdded.variant_id,
          productTitle: itemAdded.product_title || itemAdded.title,
          productImage: itemAdded.image
        }
      };

      document.dispatchEvent(new CustomEvent(this.CART_UPDATE_EVENT, {
        bubbles: true,
        detail: eventDetail
      }));

    } catch (err) {
      btn.removeAttribute('data-added');
      if (variantId) {
        delete window.recentlyAddedVariantIds[variantId.toString()];
      }
      console.error('Add to cart error:', err);
      document.dispatchEvent(new CustomEvent(this.CART_ERROR_EVENT, {
        bubbles: true,
        detail: {
          sourceId: 'you-may-also-like',
          data: { message: err.message }
        }
      }));
    } finally {
      btn.classList.remove('is-loading');
    }
  }

  #selectColorSwatch(swatch) {
    const handle = swatch.dataset.handle;
    const card = swatch.closest('.ymal-row-wrap');
    if (!card || !handle) return;

    // 1a. Update active swatch class for the container
    const swatchContainer = swatch.parentElement;
    if (swatchContainer) {
      swatchContainer.querySelectorAll('.pcs-block__swatch').forEach(s => {
        s.classList.remove('pcs-block__swatch--active');
      });
      swatch.classList.add('pcs-block__swatch--active');
    }

    // 1b. Update Custom Dropdown UI + Close Dropdown
    const dropdown = swatch.closest('details');
    if (dropdown) {
      const activeSummaryContainer = dropdown.querySelector('.ymal-custom-dropdown__summary');
      if (activeSummaryContainer) {
        // Find dot of clicked item and copy it
        const dotContainer = swatch.querySelector('.ymal-color-select-dot');
        const activeDot = activeSummaryContainer.querySelector('.ymal-color-select-dot');
        const activeLabel = activeSummaryContainer.querySelector('.ymal-custom-dropdown__label');
        if (dotContainer && activeDot) {
          activeDot.innerHTML = dotContainer.innerHTML;
        }
        if (activeLabel) {
          activeLabel.textContent = swatch.dataset.colorLabel || handle;
        }
      }
      dropdown.removeAttribute('open');
    }

    const dataEl = this.querySelector(`script[data-ymal-data="${handle}"]`);
    if (!dataEl) return;

    const data = JSON.parse(dataEl.textContent);

    // 2. Update Card UI
    if (data.id) {
      card.dataset.productId = data.id;
      const wishlistBtn = card.querySelector('.swym-add-to-wishlist-view-product');
      if (wishlistBtn) {
        wishlistBtn.dataset.productId = data.id;
        // The swym button relies on the product_ID class. Let's find and replace it
        wishlistBtn.className = wishlistBtn.className.replace(/product_\d+/, `product_${data.id}`);
      }
    }

    if (data.title) {
      const cardTitle = card.querySelector('.cart-items__product-info a');
      if (cardTitle) cardTitle.textContent = data.title;
      const quickAdd = card.querySelector('.you-may-also-like__quick-add');
      if (quickAdd) quickAdd.setAttribute('data-product-title', data.title);
      const quickAddMobile = card.querySelector('.you-may-also-like__add-overlay-wrapper');
      if (quickAddMobile) quickAddMobile.setAttribute('data-product-title', data.title);
    }
    if (data.vendor) {
      const cardVendor = card.querySelector('.cart-items__product-brand p');
      if (cardVendor) cardVendor.textContent = data.vendor;
    }
    if (data.price) {
      const priceHtml = `<text-component value="${data.price.replace(/<[^>]+>/g, '')}">${data.price}</text-component>`;
      const priceContainer = card.querySelector('.cart-items__price');
      if (priceContainer) {
        priceContainer.innerHTML = priceHtml;
      }
    }
    if (data.media) {
      const cardImage = card.querySelector('.cart-items__media-image');
      if (cardImage) {
        cardImage.src = data.media;
        cardImage.removeAttribute('srcset');
      }
    }

    if (data.promo_tags !== undefined) {
      const promoContainer = card.querySelector('.product-promo-badges');
      if (data.promo_tags && data.promo_tags.trim() !== '') {
        if (!promoContainer) {
          const titleEl = card.querySelector('.cart-items__product-info');
          if (titleEl) {
            titleEl.insertAdjacentHTML('afterend', data.promo_tags);
          }
        } else {
          promoContainer.outerHTML = data.promo_tags;
        }
      } else if (promoContainer) {
        promoContainer.remove();
      }
    }

    const cardLinks = card.querySelectorAll('a[href]');
    cardLinks.forEach(l => l.href = data.url);

    const cardLinkInput = card.querySelector('input.product-card__link');
    if (cardLinkInput) cardLinkInput.value = data.url;

    // Update Size Select
    const sizeSelect = card.querySelector('[data-ymal-size-select]');
    if (sizeSelect && data.variants) {
      let html = '';
      let firstAvailableId = null;

      data.variants.forEach((v) => {
        if (v.available && firstAvailableId === null) firstAvailableId = v.id;
        html += `<option value="${v.id}" ${v.available ? '' : 'disabled'}>${v.title}</option>`;
      });

      sizeSelect.innerHTML = html;
      sizeSelect.value = firstAvailableId || data.variants[0].id;

      // Update form hidden input
      const variantInput = card.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = sizeSelect.value;
    }
  }

  #selectSize(select) {
    const card = select.closest('.ymal-row-wrap');
    const variantInput = card.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = select.value;
  }

  #updateButtonSuccessStates() {
    const recentlyAdded = window.recentlyAddedVariantIds || {};
    const now = Date.now();

    const cards = this.querySelectorAll('.ymal-row-wrap');
    cards.forEach(card => {
      const sizeSelect = card.querySelector('[data-ymal-size-select]');
      const variantInput = card.querySelector('input[name="id"]');
      const variantId = (sizeSelect ? sizeSelect.value : variantInput?.value)?.toString();

      if (!variantId) return;

      const timestamp = recentlyAdded[variantId];
      if (timestamp && (now - timestamp < 1500)) {
        const btn = card.querySelector('.you-may-also-like__add-btn');
        if (btn) {
          btn.setAttribute('data-added', 'true');
          const remaining = 1500 - (now - timestamp);
          setTimeout(() => {
            btn.removeAttribute('data-added');
          }, remaining);
        }
      }
    });
  }

  async #handleAddToCart(e) {
    const form = e.target;
    if (!form.getAttribute('action')?.includes('/cart/add')) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const btn = form.querySelector('button');
    if (!btn) return;
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    const variantId = form.querySelector('input[name="id"]')?.value;

    // Set success state immediately on click/submit for instant visual feedback
    btn.setAttribute('data-added', 'true');
    if (variantId) {
      window.recentlyAddedVariantIds = window.recentlyAddedVariantIds || {};
      window.recentlyAddedVariantIds[variantId.toString()] = Date.now();
    }

    try {
      const formData = new FormData(form);
      /** @type {string[]} */
      const sectionsToUpdate = [];
      /** @type {NodeListOf<HTMLElement>} */
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach(comp => {
        const sId = comp.dataset.sectionId;
        if (sId && !sectionsToUpdate.includes(sId)) {
          sectionsToUpdate.push(sId);
        }
      });
      if (sectionsToUpdate.length === 0) sectionsToUpdate.push('header');
      formData.append('sections', sectionsToUpdate.join(','));

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      const itemAdded = await response.json();

      if (!response.ok) {
        throw new Error(itemAdded.description || itemAdded.message || 'Error adding to cart');
      }

      // Fetch full cart for tracking and UI sync
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      setTimeout(() => {
        btn.removeAttribute('data-added');
      }, 1500);

      // Manually construct the event to match the structure in events.js
      const source = this.dataset.source || 'you-may-also-like';
      const eventDetail = {
        resource: cart,
        sourceId: source,
        data: {
          sections: itemAdded.sections,
          source: source,
          itemCount: cart.item_count,
          variantId: itemAdded.variant_id,
          productTitle: itemAdded.product_title || itemAdded.title,
          productImage: itemAdded.image
        }
      };

      document.dispatchEvent(new CustomEvent(this.CART_UPDATE_EVENT, {
        bubbles: true,
        detail: eventDetail
      }));

    } catch (err) {
      btn.removeAttribute('data-added');
      if (variantId) {
        delete window.recentlyAddedVariantIds[variantId.toString()];
      }
      console.error('Add to cart error:', err);
      document.dispatchEvent(new CustomEvent(this.CART_ERROR_EVENT, {
        bubbles: true,
        detail: {
          sourceId: 'you-may-also-like',
          data: { message: err.message }
        }
      }));
    } finally {
      btn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('you-may-also-like-component')) {
  customElements.define('you-may-also-like-component', YouMayAlsoLikeComponent);
}
